import { httpClient2 } from '../../infrastructure/admi-client/http-client';
import {
    CostModelsDto,
    HeatPumpLoadProfileDto,
    LoadProfileDto,
    LoadProfileType,
    PVResultDto,
    RoofSegmentDto,
    SupportedBuildingFunctionDto,
} from './pv-calculator.dto';

const BASE_PATH = '/v1/potential-analysis/pv-calculator';

export const admiPVCalculatorService = {
    async doPVOptimization(
        roof_segments: RoofSegmentDto[],
        optimizer: string,
        building_function: string,
        load_profiles: LoadProfileDto[],
        chp_production_profile: number[] | null,
        funding_rate: number,
        energy_price: number | null,
        limit_100kwp: boolean,
        cost_model: string,
        target_kwp?: number,
        battery_kwp_factor?: number,
        latitude?: number,
        longitude?: number,
    ) {
        const response = await httpClient2.post<PVResultDto>(`${BASE_PATH}/pv-optimization`, {
            roof_segments,
            optimizer,
            building_function,
            load_profiles,
            funding_rate,
            energy_price,
            limit_100kwp,
            cost_model,
            target_kwp,
            battery_kwp_factor,
            latitude,
            longitude,
            chp_production_profile,
        });

        return response.data;
    },

    async getSupportedBuildingFunctions(): Promise<SupportedBuildingFunctionDto[]> {
        const response = await httpClient2.get<SupportedBuildingFunctionDto[]>(
            `${BASE_PATH}/supported-building-functions`,
        );
        return response.data;
    },

    async getCostModels(): Promise<string[]> {
        const response = await httpClient2.get<CostModelsDto>(`${BASE_PATH}/cost-models`);
        return response.data.cost_models;
    },

    errorResponseToDescription: (err: any) => {
        // Is it a PV-calculator specific error?
        if (err.detail && err.detail.error_code) {
            switch (err.detail.error_code) {
                case 'NO_SUITABLE_ROOF_SEGMENTS':
                    return 'No suitable roof segments found. This could also occur if the entered tag is not available.';
                case 'NO_PLACEMENT_STRATEGY_APPLICABLE':
                    return 'No placement found that fulfills all constraints (e.g. roof is large enough).';
                case 'NOT_WITHIN_OPTIMIZATION_CONSTRAINTS':
                    return 'The optimizer could not find a design that matches all the criteria. Please check the inputs (e.g. too small usage).';
                default:
                    return `${err.detail.description} (${err.detail.error_code})`;
            }
        }

        if (typeof err === 'string') {
            return err;
        }

        return JSON.stringify(err);
    },

    isBaseUsageEstimated(loadProfiles: LoadProfileDto[]) {
        const loadProfile = loadProfiles.find((p) => p.type === LoadProfileType.BASE);
        return typeof loadProfile?.annual_usage !== 'number';
    },

    // A heat pump profile is only usable once both figures are positive: the calculator divides the
    // heat demand by the COP, and a zero heat demand would contribute nothing to the load profile.
    // The `!(x > 0)` form also catches NaN, which is what an emptied number field parses to.
    isHeatPumpProfileIncomplete(loadProfile: HeatPumpLoadProfileDto) {
        return !(loadProfile.annual_heat_demand > 0) || !(loadProfile.annual_cop > 0);
    },

    hasIncompleteHeatPumpProfile(loadProfiles: LoadProfileDto[]) {
        return loadProfiles.some(
            (p) => p.type === LoadProfileType.HEAT_PUMP && admiPVCalculatorService.isHeatPumpProfileIncomplete(p),
        );
    },
};
